import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Form, Button, Spinner } from 'react-bootstrap';
import Swal from 'sweetalert2';
import { FaUsers } from 'react-icons/fa';
import Navigation from './Navigation';
import AdminFormLayout from './AdminFormLayout';

function AddEditDefaultMemberModules() {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditMode = !!id;

    const [roleName, setRoleName] = useState('');
    const [selectedModules, setSelectedModules] = useState([]);
    const [modules, setModules] = useState([]);
    const [loading, setLoading] = useState(true); // ✅ Show loading until data is ready

    useEffect(() => {
        const token = localStorage.getItem('sessionToken');
        if (!token) {
            navigate('/loginadmin');
            return;
        }

        fetchModules(token).then(() => {
            if (isEditMode) {
                fetchRoleDetails(token);
            } else {
                setLoading(false);
            }
        });
    }, [id, navigate, isEditMode]);

    /** ✅ Fetch Available Modules */
    const fetchModules = async (token) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}modulelist`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ limit: 100, start: 0 })
            });

            const data = await response.json();
            if (data.error === "0" && Array.isArray(data.results)) {
                setModules(data.results);
            } else {
                setModules([]);
            }
        } catch (err) {
            console.error('Failed to fetch modules:', err);
            setModules([]);
        }
    };

    /** ✅ Fetch Role Details (for Editing) */
    const fetchRoleDetails = async (token) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}get-default-member-module`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ id: parseInt(id, 10) })
            });

            const data = await response.json();
            //console.log(data);
            if (data.error === 0 && data.data) {
                setRoleName(data.data.role_name);
                setSelectedModules(data.data.modules || []);
            } else {
                setSelectedModules([]);
            }
        } catch (err) {
            console.error('Failed to fetch role details:', err);
            setSelectedModules([]);
        } finally {
            setLoading(false);
        }
    };

    /** ✅ Handle Module Selection */
    const handleModuleSelection = (moduleId) => {
        setSelectedModules(prev =>
            prev.includes(moduleId) ? prev.filter(id => id !== moduleId) : [...prev, moduleId]
        );
    };

    /** ✅ Handle Save */
    const handleSave = async () => {
        if (!roleName.trim()) {
            Swal.fire('Error!', 'Role name is required.', 'error');
            return;
        }

        const token = localStorage.getItem('sessionToken');
        const requestBody = {
            role_name: roleName,
            modules: selectedModules
        };

        const url = `${process.env.REACT_APP_API_BASE_URL}default-member-module`;
        const method = isEditMode ? 'PUT' : 'POST';

        try {
            const response = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify(isEditMode ? { id: parseInt(id, 10), ...requestBody } : requestBody)
            });

            const data = await response.json();
            //console.log(JSON.stringify(isEditMode ? { id, ...requestBody } : requestBody));
            if (data.error === 0) {
                Swal.fire('Success!', `Role ${isEditMode ? 'updated' : 'added'} successfully.`, 'success')
                    .then(() => navigate('/default-member-modules'));
            } else {
                Swal.fire('Error!', 'Failed to save role.', 'error');
            }
        } catch (err) {
            console.error('Failed to save role:', err);
            Swal.fire('Error!', 'Something went wrong.', 'error');
        }
    };

    return (
        <div>
            <Navigation />
            <AdminFormLayout
                icon={FaUsers}
                eyebrow="Service policy"
                title={isEditMode ? 'Update Member Role Policy' : 'Create Member Role Policy'}
                description="Tetapkan modul standar untuk kategori member agar layanan dan akses diterapkan secara konsisten."
                breadcrumbs={[
                    { label: 'Dashboard', to: '/dashboardadmin' },
                    { label: 'Default Member Access', to: '/default-member-modules' },
                    { label: isEditMode ? 'Update Role Policy' : 'Create Role Policy' },
                ]}
                tipsTitle="Policy Notes"
                tips={[
                    'Gunakan nama kategori atau peran yang jelas untuk member.',
                    'Tetapkan modul standar yang sesuai dengan layanan yang diberikan.',
                    'Kebijakan ini akan digunakan sebagai acuan default pada pengaturan member.',
                ]}
            >
                {loading ? (
                    <div className="text-center my-4">
                        <Spinner animation="border" />
                    </div>
                ) : (
                    <Form>
                        <div className="field-stack">
                            <Form.Label>Role name</Form.Label>
                            <Form.Control
                                type="text"
                                value={roleName}
                                onChange={(e) => setRoleName(e.target.value)}
                                placeholder="Enter role name"
                                required
                            />
                        </div>

                        <div className="field-stack">
                            <Form.Label>Modules</Form.Label>
                            <div className="check-grid">
                                {modules.length > 0 ? (
                                    modules.map(module => (
                                        <Form.Check
                                            key={module.ID}
                                            type="checkbox"
                                            label={module.Modulename}
                                            checked={selectedModules.includes(module.ID)}
                                            onChange={() => handleModuleSelection(module.ID)}
                                        />
                                    ))
                                ) : (
                                    <p>No modules available</p>
                                )}
                            </div>
                        </div>

                        <div className="form-actions">
                            <Button variant="primary" type="button" onClick={handleSave}>
                                {isEditMode ? 'Save Changes' : 'Save Policy'}
                            </Button>
                            <Button variant="secondary" onClick={() => navigate('/default-member-modules')}>
                                Cancel
                            </Button>
                        </div>
                    </Form>
                )}
            </AdminFormLayout>
        </div>
    );
}

export default AddEditDefaultMemberModules;
